import { useState } from 'react';
import { api } from '../api/client.js';

interface Props {
  postId: string;
  count: number;
  onChange: (nextCount: number) => void;
  size?: 'sm' | 'md';
}

/**
 * Звёздочка-счётчик переиспользований контента поста за текущую Warsaw-неделю.
 * Клик циклирует 0 → 1 → 2 → 3 → 0. Сброс — воскресенье 22:00 по Варшаве
 * (реализовано на сервере, клиент просто отправляет POST /reuse).
 */
export function StarButton({ postId, count, onChange, size = 'md' }: Props) {
  const [busy, setBusy] = useState(false);

  async function click() {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api<{ count: number }>(`/api/posts/${postId}/reuse`, { method: 'POST' });
      onChange(r.count);
    } catch { /* игнор */ }
    finally { setBusy(false); }
  }

  const cls = `star-btn star-count-${count}${size === 'sm' ? ' star-sm' : ''}${busy ? ' star-busy' : ''}`;
  return (
    <button
      type="button"
      className={cls}
      onClick={(e) => { e.stopPropagation(); click(); }}
      disabled={busy}
      title={`Переиспользовано на этой неделе: ${count}. Клик — увеличить (0→1→2→3→0).`}
      aria-label={`reuse count ${count}`}
    >
      <StarIcon filled={count > 0} />
      {count > 0 && <span className="star-num">{count}</span>}
    </button>
  );
}

/**
 * «Только показать» — без клика. Для рядов в топах/чартах.
 * Кнопкой не является; клик проваливается наверх (в родительскую строку).
 */
export function StarBadge({ count }: { count: number }) {
  if (!count) {
    // пустая обводка — визуально показывает что метка «есть, но 0»
    return (
      <span className="star-badge star-count-0" aria-label="reuse 0">
        <StarIcon filled={false} />
      </span>
    );
  }
  return (
    <span className={`star-badge star-count-${count}`} aria-label={`reuse ${count}`}>
      <StarIcon filled />
      <span className="star-num">{count}</span>
    </span>
  );
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}
